import { z } from 'zod';

/**
 * Validation for the Organizations add/edit form. Originally lived under
 * Admin > Directory > Organizations; ADR-114 Phase 3 moves the
 * consumer to the `VendorOrgSelector` dropdown.
 *
 * The backend serializer is `DistrictSerializer` with `fields = '__all__'`
 * (server/api/v2/serializers.py:97), so most fields are optional from the
 * API's perspective. We enforce `name` client-side and validate the rest of
 * the surfaced fields against the canonical Django choices in
 * `server/apps/schools/constants.py` (ORGANIZATION_INDUSTRIES, DISTRICT_TYPE,
 * ORGANIZATION_STATUS).
 *
 * Notes:
 * - `system_admin_email` is rendered as a comma-separated string and parsed
 *   to an array in `toWriteInput` (so this schema stays transform-free; that
 *   keeps `z.input === z.output`, which `react-hook-form`'s resolver typing
 *   requires).
 * - `auth_expiry_days` / `auth_refresh_days` / `direct_chat_unread_notification_delay`
 *   are kept as input strings here; numeric conversion happens in
 *   `toWriteInput`.
 */

// Mirrors `ORGANIZATION_STATUS` in server/apps/schools/constants.py.
export const ORGANIZATION_STATUS_OPTIONS = [
  { value: 'onboard', label: 'Onboard' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
] as const;

export type OrganizationStatus =
  (typeof ORGANIZATION_STATUS_OPTIONS)[number]['value'];

// Mirrors `ORGANIZATION_INDUSTRIES` in server/apps/schools/constants.py.
export const ORGANIZATION_INDUSTRY_OPTIONS = [
  { value: 'k12', label: 'K-12' },
  { value: 'higher-ed', label: 'Higher Ed' },
] as const;

export type OrganizationIndustry =
  (typeof ORGANIZATION_INDUSTRY_OPTIONS)[number]['value'];

// Mirrors `DISTRICT_TYPE` in server/apps/schools/constants.py.
export const ORGANIZATION_TYPE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'provider', label: 'Provider' },
  { value: 'tenant', label: 'Tenant' },
] as const;

export type OrganizationType =
  (typeof ORGANIZATION_TYPE_OPTIONS)[number]['value'];

/**
 * Django default for `auth_expiry_days` / `auth_refresh_days`
 * (`ONE_YEAR = 365` in server/apps/schools/constants.py).
 */
export const ORGANIZATION_AUTH_DAYS_DEFAULT = 365;

/**
 * Django default for direct chat unread notification delay
 * (0 == disabled; see District.direct_chat_unread_notification_delay).
 */
export const DIRECT_CHAT_UNREAD_NOTIFICATION_DELAY_DEFAULT = 0;

const optionalEmailListString = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value) return true;
      const emails = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      // Simple RFC-ish check — matches `z.string().email()` behaviour.
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emails.every((e) => emailRe.test(e));
    },
    { message: 'Enter valid email addresses, comma-separated' },
  );

const optionalNumericString = z
  .string()
  .optional()
  .refine((value) => !value || Number.isFinite(Number(value)), {
    message: 'Enter a number',
  });

const optionalNonNegativeIntString = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value) return true;
      const n = Number(value);
      return Number.isFinite(n) && Number.isInteger(n) && n >= 0;
    },
    { message: 'Enter a non-negative whole number' },
  );

export const organizationFormValidationSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    name: z.string().min(1, 'Name is required'),
    domain: z.string().optional(),
    industry: z.enum(['k12', 'higher-ed', '']).optional(),
    type: z.enum(['admin', 'provider', 'tenant', '']).optional(),
    status: z.enum(['onboard', 'online', 'offline', '']).optional(),
    system_admin_email: optionalEmailListString,
    start_alert_button_label: z.string().optional(),
    start_drill_button_label: z.string().optional(),
    start_report_button_label: z.string().optional(),
    // Optional here on purpose — a blank box is legal in the form and simply
    // means "leave this label alone" (`toWriteInput` omits it). What is NOT
    // legal is sending "" to the API; see START_BUTTON_LABEL_KEYS below.
    auth_expiry_days: optionalNumericString,
    auth_refresh_days: optionalNumericString,
    direct_chat_unread_notification_delay: optionalNonNegativeIntString,
    jwt_secret: z.string().optional(),
    is_sandbox: z.boolean().optional(),
    is_silent_drill_default: z.boolean().optional(),
    use_missing_person_chat: z.boolean().optional(),
    can_share_alerts: z.boolean().optional(),
    coverage_type: z.enum(['point_radius', 'admin_area', '']).optional(),
    latitude: optionalNumericString,
    longitude: optionalNumericString,
    service_radius_km: optionalNumericString,
    area_type: z.enum(['city', 'county', 'state', '']).optional(),
    area_name: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.coverage_type === 'admin_area') {
      if (!values.area_type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['area_type'],
          message: 'Area type is required for admin area coverage.',
        });
      }
      if (!values.area_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['area_name'],
          message: 'Area name is required for admin area coverage.',
        });
      }
    } else if (values.coverage_type === 'point_radius') {
      if (!values.latitude) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['latitude'],
          message: 'Latitude is required for point radius coverage.',
        });
      }
      if (!values.longitude) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['longitude'],
          message: 'Longitude is required for point radius coverage.',
        });
      }
      if (!values.service_radius_km) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_radius_km'],
          message: 'Service radius is required for point radius coverage.',
        });
      }
    }
  });

export type OrganizationFormValues = z.infer<
  typeof organizationFormValidationSchema
>;

/**
 * The three customizable start-button labels, as one group.
 *
 * They are declared identically on `DistrictSerializer` —
 * `CharField(required=False)`, so neither `null` nor `""` is accepted. A
 * button that renders always needs a name. Omitting the field is how a caller
 * says "leave this label alone"; on create the org then gets the Django model
 * default, which is translated server-side.
 *
 * Grouped here so the form, `toWriteInput` and their tests can't drift apart
 * on which fields carry that contract — which is exactly how the report label
 * ended up as the odd one out in the first place.
 */
export const START_BUTTON_LABEL_KEYS = [
  'start_alert_button_label',
  'start_drill_button_label',
  'start_report_button_label',
] as const satisfies readonly (keyof OrganizationFormValues)[];
